import axios, { AxiosPromise, CancelToken, CancelTokenSource, AxiosResponse } from "axios";
import React from "react";
import { useDispatch } from "react-redux";
import { startLoading, finishLoading } from "../../store/reducers/Loading";
import { handleError } from "./handleError";

interface CancelablePromise {
  promise: () => Promise<void>;
  cancel: () => void;
}

function makeCancelableWrapper<T>(
  promise: (token: CancelToken) => AxiosPromise<T>,
    dispatch: React.Dispatch<any>,
  afterCall?: (T) => void,
  disableAnimation?: boolean
): CancelablePromise {
  let cts: CancelTokenSource | undefined;
  const wrappedPromise: () => Promise<void> = async () => {
    let response: AxiosResponse | null = null;
    try {
        if (!disableAnimation) dispatch(startLoading);
      cts = axios.CancelToken.source();
      response = await promise(cts.token);
    } catch (error) {
      if (cts !== undefined && !disableAnimation)
        handleError(error, dispatch);
    } finally {
      if (!disableAnimation) dispatch(finishLoading);
    }
      if (cts !== undefined && afterCall !== undefined && response !== null)
          afterCall(response.data);
  };

  return {
    promise: wrappedPromise,
    cancel() {
      if (cts) {
        cts.cancel();
        cts = undefined;
      }
    },
  };
}

export const useCancellablePromiseWrapper = <T>() => {
  const cancelablePromise = React.useRef<CancelablePromise | undefined>(undefined);

  const d = useDispatch();

  React.useEffect(() => {
    return () => {
        if (cancelablePromise?.current) {
            cancelablePromise.current.cancel();
        }
        cancelablePromise.current = undefined;
    };
  }, []);

  return (
      p: (token: CancelToken) => AxiosPromise<T>,
      afterCall?: (T) => void,
    disableAnimation?: boolean
  ) => {
      const cPromise = makeCancelableWrapper<T>(p, d, afterCall, disableAnimation);
      if (cancelablePromise?.current) {
          cancelablePromise.current.cancel();
      }
        cancelablePromise.current = cPromise;

    return cPromise.promise();
  };
};
